'use client';

import { Fragment } from 'react';
import { cn } from '@/lib/utils';
import type { Contact } from '@/lib/services/contacts/types';
import type { VehicleInstruction } from '@/lib/services/redemption';
import {
  VEHICLE_INSTRUCTION_LABELS,
  VEHICLE_INSTRUCTION_ICONS,
  VEHICLE_INSTRUCTION_OPTIONS,
  type VehicleInstructionSectionProps,
  type VehicleTransferBeneficiaryDraft
} from './constants';
import {
  VehicleBeneficiarySelection,
  VehicleContraryIntentionCheckbox
} from './edit-sub-components';

type VehicleInstructionEditSectionProps = Omit<
  VehicleInstructionSectionProps,
  'mode' | 'milestoneSummary'
>;

export function VehicleInstructionEditSection({
  instruction,
  transferBeneficiaries,
  hasContraryIntention,
  hasLinkedLoan,
  contacts,
  onInstructionChange,
  onTransferBeneficiariesChange,
  onContraryIntentionChange
}: VehicleInstructionEditSectionProps) {
  const isTransfer = instruction === 'transfer-to-beneficiary';
  const showContraryIntention = isTransfer && !!hasLinkedLoan;

  const handleBeneficiaryToggle = (contact: Contact) => {
    const current = transferBeneficiaries ?? [];
    const exists = current.some((b) => b.contactId === contact.id);
    const updated: VehicleTransferBeneficiaryDraft[] = exists
      ? current.filter((b) => b.contactId !== contact.id)
      : [
          ...current,
          {
            contactId: contact.id,
            contact: {
              id: contact.id,
              firstName: contact.firstName ?? '',
              lastName: contact.lastName ?? '',
              organisationName: contact.organisationName ?? null
            }
          }
        ];
    onTransferBeneficiariesChange?.(updated);
  };

  return (
    <Fragment>
      <div className="border-border-subtle w-full border-t" />
      <div className="flex w-full flex-col gap-3">
        <div className="space-y-1.5">
          <label className="text-foreground-secondary text-sm font-medium">
            Instruction
          </label>
          <div className="flex flex-wrap gap-1.5">
            {VEHICLE_INSTRUCTION_OPTIONS.map((inst: VehicleInstruction) => {
              const isActive = instruction === inst;
              const Icon = VEHICLE_INSTRUCTION_ICONS[inst];
              return (
                <button
                  key={inst}
                  type="button"
                  onClick={() => onInstructionChange?.(inst)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border bg-card text-foreground-secondary hover:border-border-strong hover:bg-surface-subtle'
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {VEHICLE_INSTRUCTION_LABELS[inst]}
                </button>
              );
            })}
          </div>
        </div>

        {isTransfer && !!contacts && contacts.length > 0 && (
          <VehicleBeneficiarySelection
            contacts={contacts}
            transferBeneficiaries={transferBeneficiaries}
            onBeneficiaryToggle={handleBeneficiaryToggle}
          />
        )}

        {showContraryIntention && (
          <VehicleContraryIntentionCheckbox
            hasContraryIntention={hasContraryIntention}
            onContraryIntentionChange={onContraryIntentionChange}
          />
        )}
      </div>
    </Fragment>
  );
}
